import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, TemplateRef, inject, ViewContainerRef, ComponentRef, Type, effect } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { EntityType } from '../../objects/interface-library';
import { ContractListComponent } from '../../list-all-pages/contract-list/contract-list.component';
import { AssessmentListComponent } from '../../list-all-pages/assessment-list/assessment-list.component';
import { BaaListComponent } from '../../list-all-pages/baa-list/baa-list.component';
import { CoiListComponent } from '../../list-all-pages/coi-list/coi-list.component';
import { DateListComponent } from '../../list-all-pages/date-list/date-list.component';
import { TimesheetApprovalQueueComponent } from '../../list-all-pages/timesheet-approval-queue/timesheet-approval-queue.component';
import { TimecardApprovalQueueComponent } from '../../list-all-pages/timecard-approval-queue/timecard-approval-queue.component';
import { WorkflowTaskingListComponent } from '../../list-all-pages/workflow-tasking-list/workflow-tasking-list.component';
import { ProvisionListComponent } from '../../list-all-pages/provision-list/provision-list.component';
import { FormsModule } from '@angular/forms';

import { MatGridListModule } from '@angular/material/grid-list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { AgGridModule } from 'ag-grid-angular';

import { environment } from "../../../environments/environment";
import { SidenavContentService } from '../../service/sidenav-content.service';
import { TitleService } from '../../service/title.service';
import { MyWorkFilterService, MyWorkFilter } from '../../service/my-work-filter.service';
import { BUILD_INFO } from '../../../build-info';


@Component({
    selector: 'app-my-work',
    templateUrl: './my-work.component.html',
    styleUrls: ['./my-work.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      NgComponentOutlet,
      FormsModule,
      AgGridModule,
      MatGridListModule,
      MatSidenavModule,
      MatDividerModule,
      MatButtonModule,
      MatButtonToggleModule,
      ContractListComponent
    ]
  })
export class MyWorkComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('myWorkSidenavActions') myWorkSidenavActions!: TemplateRef<any>;
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef }) dynamicComponentContainer!: ViewContainerRef;

  private titleService = inject(TitleService);
  private sidenavContentService = inject(SidenavContentService);
  public filterService = inject(MyWorkFilterService);

  // Reference to currently loaded component
  private currentComponentRef: ComponentRef<any> | null = null;

  // Expose component type for dynamic loading
  public currentComponentType: Type<any> | null = null;

  // Environment flags
  timesheets = environment.timesheets;
  timecards = (environment as any).timecards;
  // Read as "off unless explicitly true", matching securehome.component.ts — an
  // org that never sets the key (most of them) gets Workflow off, not on.
  workflowManagement = (environment as any).workflowManagement === true;
  provisions = (environment as any).provisions === true;

  // Build stamp shown top-right so you can tell which build is live (replaces the
  // old temporary '#' marker). e.g. "4120f53* · 08-05 20:49".
  buildStamp = `${BUILD_INFO.sha}${BUILD_INFO.dirty ? '*' : ''} · ${BUILD_INFO.time.slice(5, 16).replace('T', ' ')}`;

  // Expose EntityType and MyWorkFilter to template
  EntityType = EntityType;
  MyWorkFilter = MyWorkFilter;

  constructor() {
    // Set up effect to watch for entity type changes and load appropriate component
    effect(() => {
      const entityType = this.filterService.selectedEntityType();
      this.loadComponentForEntityType(entityType);
    });
  }

  // @ViewChild('filteredContractList')
  // private contractListComponent: ContractListComponent;

  // @ViewChild('filteredBaaList')
  // private baaListComponent: BaaListComponent;

  // @ViewChild('filteredCoiList')
  // private coiListComponent: CoiListComponent;

  // @ViewChild('filteredAssessmentList')
  // private assessmentListComponent: AssessmentListComponent;

  // @ViewChild('filteredDateList')
  // private dateListComponent: DateListComponent;

  // @ViewChild('filteredTimesheetList')
  // private timesheetListComponent: TimesheetListComponent;

  // @ViewChild('filteredWorkflowTaskingList')
  // private workflowTaskingListComponent: WorkflowTaskingListComponent;


  ngOnInit(): void {
    this.titleService.setTitle('My Work');
  }

  ngAfterViewInit(): void {
    // Send the template to the service after the view is initialized
    this.sidenavContentService.updateSidenavContent(this.myWorkSidenavActions);

    // Load initial component (Contracts)
    this.loadComponentForEntityType(this.filterService.selectedEntityType());
  }

  ngOnDestroy(): void {
    // Clear the sidenav content when leaving the page
    this.sidenavContentService.updateSidenavContent(null);

    // Clean up dynamic component
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
    }
  }

  /**
   * Loads the appropriate list component based on entity type
   */
  private loadComponentForEntityType(entityType: EntityType): void {
    if (!this.dynamicComponentContainer) {
      return; // Container not ready yet
    }

    // Clear existing component
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
      this.currentComponentRef = null;
    }
    this.dynamicComponentContainer.clear();

    // Determine which component to load
    let componentType: Type<any> | null = null;

    switch (entityType) {
      case EntityType.Contract:
        componentType = ContractListComponent;
        break;
      case EntityType.Coi:
        componentType = CoiListComponent;
        break;
      case EntityType.Baa:
        componentType = BaaListComponent;
        break;
      case EntityType.Date:
        componentType = DateListComponent;
        break;
      case EntityType.Assessment:
        componentType = AssessmentListComponent;
        break;
      case EntityType.Provision:
        if (this.provisions) {
          componentType = ProvisionListComponent;
        }
        break;
      case EntityType.Workflow:
        componentType = WorkflowTaskingListComponent;
        break;
      case EntityType.Timesheet:
        if (this.timesheets) {
          componentType = TimesheetApprovalQueueComponent;
        }
        break;
      case EntityType.Timecard:
        if (this.timecards) {
          componentType = TimecardApprovalQueueComponent;
        }
        break;
      default:
        console.warn('Unknown entity type:', entityType);
        break;
    }

    // Load the component
    if (componentType) {
      this.currentComponentType = componentType;
      this.currentComponentRef = this.dynamicComponentContainer.createComponent(componentType);
    }
  }

  // Entity selection methods - these update the filter service signal
  contractsViewSelected(): void {
    this.filterService.setEntityType(EntityType.Contract);
    this.sidenavContentService.requestSidenavClose();
  }

  coiViewSelected(): void {
    this.filterService.setEntityType(EntityType.Coi);
    this.sidenavContentService.requestSidenavClose();
  }

  baaViewSelected(): void {
    this.filterService.setEntityType(EntityType.Baa);
    this.sidenavContentService.requestSidenavClose();
  }

  importantDatesViewSelected(): void {
    this.filterService.setEntityType(EntityType.Date);
    this.sidenavContentService.requestSidenavClose();
  }

  provisionsViewSelected(): void {
    this.filterService.setEntityType(EntityType.Provision);
    this.sidenavContentService.requestSidenavClose();
  }

  assessmentsViewSelected(): void {
    this.filterService.setEntityType(EntityType.Assessment);
    this.sidenavContentService.requestSidenavClose();
  }

  workflowViewSelected(): void {
    this.filterService.setEntityType(EntityType.Workflow);
    this.sidenavContentService.requestSidenavClose();
  }

  timesheetsViewSelected(): void {
    this.filterService.setEntityType(EntityType.Timesheet);
    this.sidenavContentService.requestSidenavClose();
  }

  hcpTimecardsViewSelected(): void {
    this.filterService.setEntityType(EntityType.Timecard);
    this.sidenavContentService.requestSidenavClose();
  }

  // Filter management methods
  toggleFilter(filter: MyWorkFilter): void {
    this.filterService.toggleFilter(filter);
  }

  isFilterActive(filter: MyWorkFilter): boolean {
    return this.filterService.isFilterActive(filter);
  }
}


